import { useState, useEffect, useRef } from 'react';

const VideoIntro = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [canSkip, setCanSkip] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [skipCountdown, setSkipCountdown] = useState(3);
  const [videoError, setVideoError] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const videoRef = useRef(null);

  useEffect(() => {
    // Countdown timer for skip button
    const countdownInterval = setInterval(() => {
      setSkipCountdown((prev) => {
        if (prev <= 1) {
          setCanSkip(true);
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  // Auto-skip if video doesn't load within 8 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isVideoLoaded) {
        console.warn('Video took too long to load, skipping intro');
        onComplete();
      }
    }, 8000);

    return () => clearTimeout(timeout);
  }, [isVideoLoaded, onComplete]);

  // Auto-unmute after 1 second if video is playing successfully
  useEffect(() => {
    if (isVideoLoaded && videoRef.current && !videoRef.current.paused) {
      const unmuteTimer = setTimeout(() => {
        try {
          videoRef.current.muted = false;
          setIsMuted(false);
          console.log('Video unmuted automatically');
        } catch (error) {
          console.warn('Could not unmute video:', error);
          // Keep it muted, no problem
        }
      }, 1000);

      return () => clearTimeout(unmuteTimer);
    }
  }, [isVideoLoaded]);

  const handleVideoLoaded = () => {
    console.log('Video loaded successfully');
    setIsVideoLoaded(true);
  };

  const handleVideoEnd = () => {
    setIsVisible(false);
    setTimeout(() => {
      onComplete();
    }, 500);
  };

  const handleSkip = () => {
    if (canSkip) {
      handleVideoEnd();
    }
  };

  const handleVideoError = (e) => {
    console.error('Video error:', e);
    setVideoError(true);
    // Auto-skip after 2 seconds if video fails
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-[9999] bg-black flex items-center justify-center transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <video
        ref={videoRef}
        className={`w-full h-full object-contain transition-opacity duration-500 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
        autoPlay
        muted={isMuted} // Always start muted for reliable autoplay
        playsInline
        onEnded={handleVideoEnd}
        onError={handleVideoError}
        onLoadedData={handleVideoLoaded}
        onCanPlay={handleVideoLoaded}
        preload="metadata"
      >
        <source src="/SKS-intro.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Skip Button - show when video is loaded or has error */}
      {(isVideoLoaded || videoError) && (
        <button
          onClick={handleSkip}
          className={`absolute top-8 right-8 bg-white/90 backdrop-blur-lg text-gray-800 px-6 py-3 rounded-full hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl font-medium ${
            canSkip ? 'opacity-100 hover:scale-105' : 'opacity-75 cursor-not-allowed'
          }`}
          disabled={!canSkip}
        >
          {canSkip ? 'Skip' : `Skip (${skipCountdown})`}
        </button>
      )}

      {/* Subtle Sound Control - only show when video is loaded and playing */}
      {isVideoLoaded && !videoError && (
        <button
          onClick={toggleMute}
          className="absolute bottom-8 right-8 bg-white/90 hover:bg-white text-gray-800 p-4 rounded-full transition-all duration-300 backdrop-blur-lg shadow-lg hover:shadow-xl hover:scale-105"
          title={isMuted ? 'Enable Sound' : 'Mute'}
        >
          {isMuted ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.823L4.216 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.216l4.167-3.823a1 1 0 011.617.823zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 11-1.414-1.414A7.972 7.972 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
              <path d="M15.536 15.536L4.464 4.464" stroke="currentColor" strokeWidth="2"/>
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.823L4.216 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.216l4.167-3.823a1 1 0 011.617.823zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 11-1.414-1.414A7.972 7.972 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      )}

      {/* Loading fallback */}
      {!isVideoLoaded && !videoError && (
        <div className="absolute inset-0 bg-gradient-to-br from-white via-amber-50 to-yellow-50 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="relative mb-8">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-amber-200 border-t-amber-600 mx-auto"></div>
            </div>
            <h2 className="text-3xl font-bold mb-3 text-gray-900 font-serif">Shehrullah Khidmat Sharaf</h2>
            <p className="text-lg text-gray-600 mb-2">Preparing your experience</p>
            <p className="text-sm text-amber-600 font-medium">Community service begins with unity</p>
          </div>
        </div>
      )}

      {/* Video error fallback */}
      {videoError && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-red-50 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900 font-serif">Unable to Load Video</h2>
            <p className="text-gray-600 mb-4">We're having trouble loading the intro video</p>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <p className="text-sm text-amber-700 font-medium">Redirecting to the main application...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoIntro;